import keys from '@debbie/common/dist/cache/keys.js';
import { CaseTag } from '@debbie/common/dist/cases/index.js';
import { organizeBilling } from '@debbie/common/dist/economy/billing/organize.js';
import { CaseVoucherSource, PaymentType, } from '@debbie/common/dist/economy/case-vouchers/index.js';
import { NO_CASE_CONDITIONS } from '@debbie/common/dist/economy/helpers/interest/index.js';
import { isPayment } from '@debbie/common/dist/economy/helpers/vouchers.js';
import { CaseVoucherTags } from '@debbie/common/dist/economy/index.js';
import { VoucherCategory, VoucherTypeIds, VoucherTypeSetting, } from '@debbie/common/dist/economy/vouchers/index.js';
import { CaseVoucherCategories, DebtorImposedBy, } from '@debbie/common/dist/economy/vouchers/setup.js';
import { Currency } from '@debbie/common/dist/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as billingModelsAPI from '@debbie/cortex/dist/api/billing-models.js';
import { billingModelsEntity } from '@debbie/cortex/dist/billing-models/index.js';
import { useShareVouchers } from '@debbie/cortex/dist/heavy-calculations/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenant, useTenantId } from '@debbie/cortex/dist/tenant/index.js';
import { useVoucherTypes } from '@debbie/cortex/dist/voucher-types/index.js';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import { errorNotification } from '../../../utils/errors.js';
import { Col, Row } from '../../Grid/index.js';
import Plus from '../../Icons/Plus.js';
import { Button, Checkbox, Input, RadioSelect, Well } from '../../index.js';
import DepositShareCmp from '../DepositShare.js';
import OrderEditor from './OrderEditor.js';
import ShareEditor, { ShareStyled } from './ShareEditor.js';
const VoucherCategoriesToExcludeFromUI = [VoucherCategory.TRANSACTION];
const BillingModel = (props) => {
    const { editable: _editable, billingModel } = props;
    const lang = useSelector((s) => s.lang);
    const tenant = useTenant();
    const meRole = useRole();
    const tenantId = useTenantId();
    const voucherTypesLoadable = useVoucherTypes();
    const voucherTypes = h.hasData(voucherTypesLoadable)
        ? voucherTypesLoadable.data
        : null;
    // Withdrawals and every payment type are never distribution targets, so
    // they stay out of the order editor and are pinned in front of the priority
    const pinnedVoucherTypeIds = [
        VoucherTypeIds.WITHDRAWAL,
        ...(voucherTypes ?? []).filter(isPayment).map((t) => t.voucherTypeId),
    ];
    const [name, setName] = useState('');
    const [preventNegativeCreditorBalance, setPreventNegativeCreditorBalance] = useState(false);
    const [simulateAmount, setSimulateAmount] = useState('37000');
    const [simulateIsCompany, setSimulateIsCompany] = useState(false);
    const [simulateSurveillance, setSimulateSurveillance] = useState(false);
    const [collectionCommissionEditor, setCollectionCommissionEditor] = useState(true);
    const [collectionCommissionShares, setCollectionCommissionShares] = useState([
        {
            shareId: crypto.randomUUID(),
            categories: [VoucherCategory.COST],
            caseVoucherSource: CaseVoucherSource.Collector,
        },
    ]);
    const getVoucherTypeList = () => (voucherTypes || [])
        .filter((voucherType) => [
        VoucherCategory.COST,
        VoucherCategory.COURTCOST,
        VoucherCategory.PRINCIPAL,
        VoucherCategory.INTEREST,
    ].includes(voucherType.category))
        .sort((a, b) => a.category.charCodeAt(0) - b.category.charCodeAt(0))
        .map((voucherType) => {
        return {
            id: voucherType.voucherTypeId,
            content: (React.createElement(DraggableBox, null,
                lang.economyCategories[voucherType.category],
                " -",
                ' ',
                lang.economyVoucherTypeLabels[voucherType.voucherTypeId] ||
                    voucherType.label)),
        };
    });
    const [orderSettings, setOrderSettings] = useState({
        byVoucherType: false,
        voucherTypeIdsOrdered: [],
        voucherCategoriesOrdered: CaseVoucherCategories.filter((voucherCategory) => !VoucherCategoriesToExcludeFromUI.includes(voucherCategory)).map((voucherCategory) => ({
            id: voucherCategory,
            content: (React.createElement(DraggableBox, null, lang.economyCategories[voucherCategory])),
        })),
        voucherImposedBy: DebtorImposedBy.map((tag) => ({
            id: tag,
            content: React.createElement(DraggableBox, null, lang.caseVoucherTags[tag]),
        })),
        voucherDate: [lang.tenants.oldestFirst, lang.tenants.newestFirst].map((s) => ({
            id: s,
            content: React.createElement(DraggableBox, null, s),
        })),
    });
    useEffect(() => {
        if (voucherTypes) {
            setOrderSettings({
                ...orderSettings,
                voucherTypeIdsOrdered: getVoucherTypeList(),
            });
        }
    }, [voucherTypes]);
    useEffect(() => {
        if (billingModel && voucherTypes) {
            setName(billingModel.name);
            setPreventNegativeCreditorBalance(!!billingModel.model.preventNegativeCreditorBalance);
            setCollectionCommissionShares(billingModel.model.share || []);
            setOrderSettings({
                byVoucherType: !!billingModel.model.voucherTypePriority,
                voucherCategoriesOrdered: (billingModel.model.voucherCategoryPriority || [])
                    .filter((id) => !VoucherCategoriesToExcludeFromUI.includes(id))
                    .map((id) => ({
                    id,
                    content: (React.createElement(DraggableBox, null, lang.economyCategories[id])),
                })),
                voucherTypeIdsOrdered: (billingModel.model.voucherTypePriority || [])
                    .filter((id) => !pinnedVoucherTypeIds.includes(id))
                    .map((id) => ({
                    id,
                    content: (React.createElement(DraggableBox, null, lang.economyVoucherTypeLabels[id])),
                })),
                voucherDate: (billingModel.model.voucherDatePriorityNewFirst
                    ? [lang.tenants.newestFirst, lang.tenants.oldestFirst]
                    : [lang.tenants.oldestFirst, lang.tenants.newestFirst]).map((s) => ({
                    id: s,
                    content: React.createElement(DraggableBox, null, s),
                })),
                voucherImposedBy: (billingModel.model.voucherTagPriority || [])[0] ===
                    CaseVoucherTags.IMPOSEDBYCOLLECTOR
                    ? [
                        {
                            id: CaseVoucherTags.IMPOSEDBYCOLLECTOR,
                            content: (React.createElement(DraggableBox, null, lang.caseVoucherTags[CaseVoucherTags.IMPOSEDBYCOLLECTOR])),
                        },
                        {
                            id: CaseVoucherTags.IMPOSEDBYCREDITOR,
                            content: (React.createElement(DraggableBox, null, lang.caseVoucherTags[CaseVoucherTags.IMPOSEDBYCREDITOR])),
                        },
                    ]
                    : [
                        {
                            id: CaseVoucherTags.IMPOSEDBYCREDITOR,
                            content: (React.createElement(DraggableBox, null, lang.caseVoucherTags[CaseVoucherTags.IMPOSEDBYCREDITOR])),
                        },
                        {
                            id: CaseVoucherTags.IMPOSEDBYCOLLECTOR,
                            content: (React.createElement(DraggableBox, null, lang.caseVoucherTags[CaseVoucherTags.IMPOSEDBYCOLLECTOR])),
                        },
                    ],
            });
        }
    }, [billingModel, voucherTypes]);
    const updateCollectionCommissionShare = (index, change) => {
        setCollectionCommissionShares(collectionCommissionShares.map((s, i) => {
            if (i === index) {
                return {
                    ...s,
                    ...change,
                };
            }
            return s;
        }));
    };
    const getNewBillingModel = () => ({
        name,
        billingModelId: 'no-billing-model-id',
        createdAt: 'no-created-at',
        createdBy: null,
        archived: false,
        public: false,
        model: {
            preventNegativeCreditorBalance,
            share: collectionCommissionShares,
            voucherCategoryPriority: !orderSettings.byVoucherType
                ? [
                    ...VoucherCategoriesToExcludeFromUI,
                    ...orderSettings.voucherCategoriesOrdered.map((i) => i.id),
                ]
                : undefined,
            voucherTypePriority: orderSettings.byVoucherType
                ? [
                    ...pinnedVoucherTypeIds,
                    ...orderSettings.voucherTypeIdsOrdered.map((i) => i.id),
                ]
                : undefined,
            voucherDatePriorityNewFirst: orderSettings.voucherDate[0].id === lang.tenants.newestFirst,
            voucherTagPriority: orderSettings.voucherImposedBy.map((i) => i.id),
            // The editor has no controls for these, so an edited model keeps its own
            from: billingModel?.model.from ?? {
                settings: [VoucherTypeSetting.Payment],
            },
            exclude: billingModel?.model.exclude ?? {
                voucherTypeIds: [VoucherTypeIds.WITHDRAWAL],
            },
            toVoucherTypeId: VoucherTypeIds.COLLECTIONCOMMISSION,
            clientDepositToVoucherTypeId: VoucherTypeIds.COLLECTIONCOMMISSIONCLIENTDEPOSIT,
        },
    });
    const caseVouchers = getSampleVouchers();
    const amount = parseFloat(simulateAmount) * 100;
    const sampleDeposit = getSampleDeposit(typeof amount === 'number' && !isNaN(amount) ? amount : 0);
    const billingVouchersLoader = useShareVouchers({ ...sampleDeposit, caseId: 'no-case-id' }, [], {
        trailingYears: 10,
    }, {
        tags: simulateSurveillance
            ? [
                {
                    tag: CaseTag.SURVEILLANCE,
                    from: moment().subtract(10, 'year').format(),
                    to: null,
                },
            ]
            : [],
        billingStartDate: null,
        isCompany: simulateIsCompany,
    }, getNewBillingModel(), [], // No fixed distributions for billing model simulation
    NO_CASE_CONDITIONS, caseVouchers.concat(sampleDeposit));
    const [_newBillingVouchers, _newResidueBillingVouchers, _newOverheadBillingVouchers,] = h.hasData(billingVouchersLoader)
        ? billingVouchersLoader.data
        : [[], [], []];
    const billingVouchers = _newBillingVouchers
        .concat(_newResidueBillingVouchers)
        .concat(_newOverheadBillingVouchers)
        .map((newBillingVoucher) => {
        let category = null;
        switch (newBillingVoucher.voucherTypeId) {
            case VoucherTypeIds.COLLECTIONCOMMISSION:
            case VoucherTypeIds.COLLECTIONCOMMISSIONSURVEILLANCE:
                category = VoucherCategory.COLLECTIONCOMMISSION;
                break;
            case VoucherTypeIds.OVERPAYMENT:
                category = VoucherCategory.OVERPAYMENT;
                break;
            case VoucherTypeIds.DEPOSITPAYOUT:
                category = VoucherCategory.PAYOUT;
                break;
            default:
                category = VoucherCategory.FEE;
                break;
        }
        if (!category) {
            throw new Error('Category not found');
        }
        if (!tenantId) {
            throw new Error('No tenant id');
        }
        const billingVoucher = {
            ...newBillingVoucher,
            id: Math.random(),
            label: '',
            setting: null,
            referenceId: null,
            amount: newBillingVoucher.amount,
            caseVoucherId: newBillingVoucher.caseVoucherId || null,
            depositCaseVoucherId: newBillingVoucher.depositCaseVoucherId || null,
            billingModelId: newBillingVoucher.billingModelId || null,
            billingId: newBillingVoucher.billingId || null,
            category,
            deletingBillingVoucherId: newBillingVoucher.deletingBillingVoucherId || null,
            billingReasonId: newBillingVoucher.billingReasonId || null,
            shareId: newBillingVoucher.shareId || null,
            billingAppendixId: newBillingVoucher.billingAppendixId || null,
            text: null,
            isAdjustment: false,
            details: {},
            createdAt: new Date().toISOString(),
            billingVoucherId: crypto.randomUUID(),
            tenantId,
            billAfter: null,
            caseId: 'no-case-id',
            createdBy: null,
            currency: tenant?.currency || Currency.EUR,
            voucherTypeId: newBillingVoucher.voucherTypeId,
            vatOfBillingVoucherId: newBillingVoucher.vatOfBillingVoucherId || null,
            date: new Date().toISOString(),
            deletedAt: null,
            deletedBy: null,
            migratedId: null,
        };
        return billingVoucher;
    });
    const { depositShare } = organizeBilling(billingVouchers);
    const editable = !!((_editable || !!props.onCreate) &&
        (!props.billingModel || !props.billingModel.public) &&
        meRole &&
        isAdmin(meRole));
    const shareValid = useMemo(() => {
        return !collectionCommissionShares.find((s) => {
            const forceCollectionCommissionShareMissingInfo = s.forceCollectionCommissionBilling
                ? !s.forceCollectionCommissionBilling.withCaseTag ||
                    !s.forceCollectionCommissionBilling.voucherTypeId
                : false;
            return forceCollectionCommissionShareMissingInfo;
        });
    }, [collectionCommissionShares]);
    const updateNameMutation = useMutation((newName) => {
        if (!props.billingModel) {
            throw new Error('No billing model');
        }
        return billingModelsAPI.patch(props.billingModel.billingModelId, {
            name: newName,
        });
    }, {
        onSuccess: () => {
            if (props.billingModel) {
                q.invalidate(keys.write.modify(billingModelsEntity, props.billingModel.billingModelId));
            }
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.billingModel);
        },
    });
    const saveModelMutation = useMutation(() => {
        if (!props.billingModel) {
            throw new Error('No billing model');
        }
        return billingModelsAPI.patch(props.billingModel.billingModelId, {
            name,
            model: getNewBillingModel().model,
        });
    }, {
        onSuccess: () => {
            if (props.billingModel) {
                q.invalidate(keys.write.modify(billingModelsEntity, props.billingModel.billingModelId));
            }
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.billingModel);
        },
    });
    const createModelMutation = useMutation(async () => {
        const newBillingModel = getNewBillingModel();
        const res = await billingModelsAPI.create({
            model: newBillingModel.model,
            name: newBillingModel.name,
        });
        return { ...newBillingModel, ...res };
    }, {
        onSuccess: (result) => {
            q.invalidate(keys.write.create(billingModelsEntity));
            if (props.onCreate) {
                props.onCreate(result);
            }
        },
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.billingModel);
        },
    });
    return (React.createElement(Row, null,
        React.createElement(Col, { width: "6" },
            React.createElement(Well, null,
                React.createElement(Input, { placeholder: lang.billingModels.name, onChange: (e) => setName(e.target.value), value: name, disabled: !editable, onSubmit: props.billingModel?.name !== name &&
                        meRole &&
                        isAdmin(meRole) &&
                        !props.onCreate
                        ? () => updateNameMutation.mutate(name)
                        : undefined })),
            React.createElement("div", { style: { marginBottom: '20px', marginTop: '20px' } },
                React.createElement(RadioSelect, { onClick: () => setCollectionCommissionEditor(true), active: collectionCommissionEditor, label: lang.tenants.collectionCommission }),
                React.createElement(RadioSelect, { onClick: () => setCollectionCommissionEditor(false), active: !collectionCommissionEditor, label: lang.tenants.depositOrder })),
            collectionCommissionEditor ? (React.createElement("div", null,
                collectionCommissionShares.map((share, index) => (React.createElement(Well, { style: { marginBottom: `${THEME.GRID_SPACING}px` }, key: share.shareId },
                    React.createElement(ShareEditor, { share: share, onChange: (change) => updateCollectionCommissionShare(index, change), onDelete: () => {
                            setCollectionCommissionShares(collectionCommissionShares.filter((_, i) => i != index));
                        }, readOnly: !editable })))),
                editable && (React.createElement(ShareStyled, { style: { cursor: 'pointer', marginBottom: '0px' }, onClick: () => {
                        setCollectionCommissionShares(collectionCommissionShares.concat({
                            shareId: crypto.randomUUID(),
                            caseVoucherSource: CaseVoucherSource.Collector,
                        }));
                    } },
                    React.createElement("b", null,
                        React.createElement(Plus, { style: {
                                margin: '0.2em 0 -0.2em',
                            } }),
                        ' ',
                        lang.billingModels.addShare))))) : (React.createElement(Well, null,
                React.createElement(OrderEditor, { orderSettings: orderSettings, setOrderSettings: (change) => setOrderSettings({ ...orderSettings, ...change }), readOnly: !editable }))),
            React.createElement("br", null),
            props.editable && !props.billingModel?.public && (React.createElement(Button, { disabled: !(name && shareValid), block: true, loading: saveModelMutation.isLoading, onClick: () => saveModelMutation.mutate() }, lang.inputs.save)),
            props.onCreate && (React.createElement(Button, { disabled: !(name && shareValid), block: true, loading: createModelMutation.isLoading, onClick: () => createModelMutation.mutate() }, lang.billingModels.createBillingModel))),
        React.createElement(Col, { width: "6", style: { position: 'sticky', top: '20px' } },
            React.createElement("div", null,
                React.createElement("h3", null, lang.billingModels.simulateBilling),
                React.createElement(Row, null,
                    React.createElement(Col, { width: "6" },
                        React.createElement(Checkbox, { checked: simulateIsCompany, onChange: (e) => setSimulateIsCompany(e.target.checked), placeholder: lang.billingModels.simulateCompany })),
                    React.createElement(Col, { width: "6" },
                        React.createElement(Checkbox, { checked: simulateSurveillance, onChange: (e) => setSimulateSurveillance(e.target.checked), placeholder: lang.billingModels.simulateSurveillance }))),
                React.createElement(Input, { placeholder: lang.billingModels.simulateAmount, type: "number", onChange: (e) => setSimulateAmount(e.target.value), value: simulateAmount }),
                !!depositShare.length &&
                    depositShare.map((dS) => (React.createElement(DepositShareCmp, { depositShare: dS, demo: true })))))));
};
export default BillingModel;
const getSampleVoucher = (caseVoucherId, id, voucherTypeId, category, amount, date, referenceId, source, referenceCaseVoucherId) => ({
    amount,
    category,
    date,
    referenceId: referenceId || null,
    tags: [],
    voucherTypeId,
    referenceCaseVoucherId: referenceCaseVoucherId || null,
    caseVoucherId,
    createdAt: date,
    currency: Currency.EUR,
    id,
    label: '',
    setting: null,
    appendix: null,
    state: null,
    caseId: 'no-case-id',
    createdBy: null,
    details: null,
    dueDate: null,
    expirationDate: null,
    expirationLength: null,
    expirationManualSet: false,
    expired: false,
    importId: null,
    cardType: null,
    deletedAt: null,
    deletedBy: null,
    depositOnPaymentPlanEntryId: null,
    interest: null,
    interestEndDate: null,
    interestStartDate: null,
    irrecoverableDate: null,
    lineItems: null,
    source: source || CaseVoucherSource.Collector,
    tenantId: 'no-tenant-id',
    text: null,
    transactionAccountId: null,
    paymentType: null,
    paymentMethodId: null,
    exported: false,
    transactionImportId: null,
    expirationFromDate: null,
    settingDetails: null,
    associationId: null,
});
const getSampleDeposit = (amount) => ({
    ...getSampleVoucher('a', 1, VoucherTypeIds.DEPOSIT, VoucherCategory.TRANSACTION, amount, '2023-05-25T00:00:00.000Z', '1', CaseVoucherSource.Collector, undefined),
    paymentType: PaymentType.Mastercard,
    setting: VoucherTypeSetting.Payment,
    settingDetails: { interruptsLimitationPeriod: true },
});
const getSampleVouchers = () => {
    return [
        getSampleVoucher('b', 2, VoucherTypeIds.INVOICE, VoucherCategory.PRINCIPAL, -1200000, '2023-05-15T00:00:01.000Z', '1', CaseVoucherSource.Creditor),
        getSampleVoucher('c', 3, VoucherTypeIds.PROCESSINTEREST, VoucherCategory.INTEREST, -343276, '2023-05-15T00:00:02.000Z', undefined, undefined, 'b'),
        getSampleVoucher('d', 4, VoucherTypeIds.INVOICE, VoucherCategory.PRINCIPAL, -1200000, '2023-05-16T00:00:03.000Z', '2', CaseVoucherSource.Creditor),
        getSampleVoucher('e', 5, VoucherTypeIds.INVOICE, VoucherCategory.PRINCIPAL, -1200000, '2023-05-17T00:00:04.000Z', '3', CaseVoucherSource.Creditor),
        getSampleVoucher('f', 6, VoucherTypeIds.REMINDERFEE, VoucherCategory.COST, -20000, '2023-05-18T00:00:05.000Z', null, CaseVoucherSource.Creditor),
        getSampleVoucher('g', 7, VoucherTypeIds.REMINDERFEE, VoucherCategory.COST, -10000, '2023-05-19T00:00:06.000Z'),
        getSampleVoucher('h', 8, VoucherTypeIds.COLLECTIONCOST, VoucherCategory.COST, -160000, '2023-05-20T00:00:07.000Z'),
        getSampleVoucher('i', 9, VoucherTypeIds.COLLECTIONFEE, VoucherCategory.COST, -10000, '2023-05-20T00:00:08.000Z'),
        getSampleVoucher('j', 10, VoucherTypeIds.BAILIFFSFEE, VoucherCategory.COURTCOST, -75000, '2023-05-20T00:00:09.000Z'),
    ];
};
const DraggableBoxStyled = styled.div `
  background: White;
  padding: 15px;
  border-radius: 3px;
`;
const DraggableBox = (props) => {
    return (React.createElement("div", { style: { paddingBottom: '5px' } },
        React.createElement(DraggableBoxStyled, null, props.children)));
};
//# sourceMappingURL=BillingModel.js.map